import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi, ApiError } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useFeed } from '@/lib/use-feed';
import { ArticleFeed } from '@/components/content';
import { PageHeader, Section } from './page-parts';
import NotFound from './NotFound';

/**
 * `/:locale/tag/:slug`. The server resolves the tag and its first page; the feed below
 * keeps paging from the same cursor endpoint the homepage uses.
 */
export default function TagPage() {
  const { locale, t } = useLocale();
  const { slug = '' } = useParams();

  const { data, isError, error } = useQuery({
    queryKey: queryKeys.tag(locale, slug),
    queryFn: () => publicApi.tag(locale, slug),
    staleTime: STALE.feed,
  });

  const feed = useFeed(locale, { tag: slug, limit: 12 });

  if (isError && error instanceof ApiError && error.status === 404) return <NotFound />;

  return (
    <>
      <PageHeader title={data ? `#${data.tag.name}` : `#${slug.replace(/-/g, ' ')}`} />
      <Section>
        <ArticleFeed
          articles={feed.articles}
          isPending={feed.isPending}
          isError={feed.isError}
          hasNextPage={Boolean(feed.hasNextPage)}
          isFetchingNextPage={feed.isFetchingNextPage}
          onLoadMore={() => void feed.fetchNextPage()}
          onRetry={() => void feed.refetch()}
          emptyTitle={t('empty.noStories')}
        />
      </Section>
    </>
  );
}
